/**
 * FlowSummaryStrip — Phase E (2026-05-04)
 *
 * Compact 4-counter strip for the Inhouse Section dashboard. Surfaces
 * how many bundles have moved through each of the four flows
 * (REGULAR / FLEXI / SHIRTING / RUNNING_MTR) over the trailing 7-day
 * window.
 *
 * Backed by GET /api/inhouse/bundle-receipt/flow-summary?days=7. The
 * endpoint always returns a deterministic 4-row payload so the strip
 * never has to reason about missing flows.
 */
import React, { useEffect, useState } from 'react';
import apiClient from '@/services/apiClient';
import { Loader2, Repeat, Layers, Shirt, Ruler, RotateCcw, Truck } from 'lucide-react';

const FLOW_META = {
  REGULAR: {
    label: 'Regular',
    description: 'PI → Folding → Segregation → Assortment (auto-pressed)',
    Icon: Repeat,
    bg: 'bg-slate-100',
    fg: 'text-slate-700',
    iconBg: 'bg-slate-500',
  },
  FLEXI: {
    label: 'Flexi',
    description: 'Skips Pressing — auto-pressed at Folding',
    Icon: Layers,
    bg: 'bg-emerald-50',
    fg: 'text-emerald-700',
    iconBg: 'bg-emerald-500',
  },
  SHIRTING: {
    label: 'Shirting',
    description: 'Mtr → pcs at PI; rest of pipeline runs',
    Icon: Shirt,
    bg: 'bg-blue-50',
    fg: 'text-blue-700',
    iconBg: 'bg-blue-500',
  },
  RUNNING_MTR: {
    label: 'Running Mtr',
    description: '1 USN per bundle; manual IT to Flexi',
    Icon: Ruler,
    bg: 'bg-amber-50',
    fg: 'text-amber-700',
    iconBg: 'bg-amber-500',
  },
  RE_FOLDING: {
    label: 'Re-Folding',
    description: 'Skips PI — bundle re-enters at Folding',
    Icon: RotateCcw,
    bg: 'bg-rose-50',
    fg: 'text-rose-700',
    iconBg: 'bg-rose-500',
  },
  JOB_WORKER: {
    label: 'Job Worker',
    description: 'Sent out for Folding & Pressing; returns to Damage / Assortment',
    Icon: Truck,
    bg: 'bg-indigo-50',
    fg: 'text-indigo-700',
    iconBg: 'bg-indigo-500',
  },
};

const FlowSummaryStrip = ({ days = 7 }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await apiClient.get(`/inhouse/bundle-receipt/flow-summary?days=${days}`);
        if (cancelled) return;
        setRows(r.data.rows || []);
        setTotal(r.data.total_bundles || 0);
        setError('');
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Failed to load flow summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="flow-summary-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading flow summary…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-red-600" data-testid="flow-summary-error">{error}</div>
    );
  }

  return (
    <div data-testid="flow-summary-strip">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          Flow activity · last {days} days
        </h3>
        <span className="text-xs text-gray-500" data-testid="flow-summary-total">
          {total} bundle{total === 1 ? '' : 's'} total
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {rows.map((row) => {
          const meta = FLOW_META[row.flow_type] || FLOW_META.REGULAR;
          const { Icon } = meta;
          return (
            <div
              key={row.flow_type}
              className={`${meta.bg} rounded-lg p-3 border border-transparent hover:border-gray-200 transition`}
              data-testid={`flow-summary-${row.flow_type.toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-9 h-9 ${meta.iconBg} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className={`text-2xl font-bold ${meta.fg}`} data-testid={`flow-count-${row.flow_type.toLowerCase()}`}>
                  {row.bundle_count}
                </div>
              </div>
              <div className={`mt-1.5 text-sm font-semibold ${meta.fg}`}>{meta.label}</div>
              <div className="text-[11px] text-gray-600 leading-snug">
                {meta.description}
              </div>
              {row.flow_type === 'SHIRTING' && row.total_received_mtr > 0 && (
                <div className="text-[10px] text-blue-700 mt-1">
                  ≈ {row.total_received_mtr.toFixed(1)} m received · {row.total_usn_count} pcs minted
                </div>
              )}
              {row.flow_type === 'RUNNING_MTR' && row.total_received_mtr > 0 && (
                <div className="text-[10px] text-amber-700 mt-1">
                  ≈ {row.total_received_mtr.toFixed(1)} m total
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlowSummaryStrip;
